import {formatNumber, formatPercentage} from "@/adapters/push.adapter";
import {BaseAlert} from "@/components/BaseAlert";
import {BaseBadge} from "@/components/BaseBadge";
import {BaseButton} from "@/components/BaseButton";
import {BaseCard} from "@/components/BaseCard";
import {PATHS} from "@/constants/routes";
import {useCampaign, useCancelCampaign, useSendCampaign} from "@/hooks/usePush";
import {formatDateTime} from "@/utils/date.utils";
import {AlertCircle, ArrowLeft, CheckCircle, Eye, Send, X, XCircle} from "lucide-react";
import {useState} from "react";
import {useNavigate, useParams} from "react-router";

export const CampaignDetail = () => {
  const {id} = useParams<{id: string}>();
  const navigate = useNavigate();
  const {data: campaign, isLoading} = useCampaign(id ?? "");
  const sendCampaign = useSendCampaign();
  const cancelCampaign = useCancelCampaign();
  const [showConfirmSend, setShowConfirmSend] = useState(false);
  const [showConfirmCancel, setShowConfirmCancel] = useState(false);
  const [alertState, setAlertState] = useState<{
    open: boolean;
    variant: "success" | "danger";
    title: string;
    description?: string;
  }>({open: false, variant: "success", title: ""});

  const handleSend = async () => {
    if (!id) return;
    try {
      await sendCampaign.mutateAsync(id);
      setShowConfirmSend(false);
      setAlertState({
        open: true,
        variant: "success",
        title: "Campaign sent",
        description: "Campaign sent successfully!",
      });
    } catch {
      setAlertState({open: true, variant: "danger", title: "Error", description: "Failed to send campaign"});
    }
  };

  const handleCancel = async () => {
    if (!id) return;
    try {
      await cancelCampaign.mutateAsync(id);
      setShowConfirmCancel(false);
      setAlertState({
        open: true,
        variant: "success",
        title: "Campaign cancelled",
        description: "Campaign cancelled successfully!",
      });
    } catch {
      setAlertState({open: true, variant: "danger", title: "Error", description: "Failed to cancel campaign"});
    }
  };

  if (isLoading) {
    return (
      <div className="flex flex-col gap-6">
        <p
          className="text-sm"
          style={{color: "var(--color-text-secondary)"}}
        >
          Loading campaign...
        </p>
      </div>
    );
  }

  if (!campaign) {
    return (
      <div className="flex flex-col gap-6">
        <p
          className="text-sm"
          style={{color: "var(--color-danger)"}}
        >
          Campaign not found
        </p>
      </div>
    );
  }

  const canSend = campaign.status === "draft" || campaign.status === "scheduled";
  const canCancel = campaign.status === "draft" || campaign.status === "scheduled";

  return (
    <div className="flex flex-col gap-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <BaseButton
            variant="ghost"
            size="sm"
            onClick={() => navigate(PATHS.PUSH_CAMPAIGNS)}
            leftIcon={<ArrowLeft size={18} />}
          >
            Back
          </BaseButton>
          <div>
            <h1
              className="text-2xl font-bold"
              style={{color: "var(--color-text)"}}
            >
              {campaign.title}
            </h1>
            <p
              className="text-sm mt-1"
              style={{color: "var(--color-text-secondary)"}}
            >
              Campaign ID: {campaign.id}
            </p>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <BaseBadge
            label={campaign.status}
            variant={
              campaign.status === "sent"
                ? "success"
                : campaign.status === "failed"
                  ? "danger"
                  : campaign.status === "sending"
                    ? "warning"
                    : "neutral"
            }
          />
          {canSend && (
            <BaseButton
              variant="primary"
              onClick={() => setShowConfirmSend(true)}
              leftIcon={<Send size={18} />}
              disabled={sendCampaign.isPending}
            >
              Send Now
            </BaseButton>
          )}
          {canCancel && (
            <BaseButton
              variant="danger"
              onClick={() => setShowConfirmCancel(true)}
              leftIcon={<X size={18} />}
              disabled={cancelCampaign.isPending}
            >
              Cancel
            </BaseButton>
          )}
        </div>
      </div>

      {/* Campaign Info */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <BaseCard padding="md">
          <h2
            className="text-base font-semibold mb-3"
            style={{color: "var(--color-text)"}}
          >
            Campaign Information
          </h2>
          <div className="flex flex-col gap-2.5 text-sm">
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Category:</span>
              <BaseBadge
                label={campaign.category}
                variant="neutral"
              />
            </div>
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Audience Type:</span>
              <span style={{color: "var(--color-text)"}}>{campaign.audienceType}</span>
            </div>
            <div className="flex justify-between">
              <span style={{color: "var(--color-text-secondary)"}}>Created:</span>
              <span style={{color: "var(--color-text)"}}>{formatDateTime(campaign.createdAt)}</span>
            </div>
            {campaign.sentAt && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Sent:</span>
                <span style={{color: "var(--color-text)"}}>{formatDateTime(campaign.sentAt)}</span>
              </div>
            )}
          </div>
        </BaseCard>

        {/* Content Preview */}
        <BaseCard padding="md">
          <h2
            className="text-base font-semibold mb-3"
            style={{color: "var(--color-text)"}}
          >
            Content Preview
          </h2>
          <div
            className="p-4 rounded-lg"
            style={{backgroundColor: "var(--color-surface-2)"}}
          >
            <p
              className="text-sm font-semibold mb-1"
              style={{color: "var(--color-text)"}}
            >
              {campaign.title}
            </p>
            <p
              className="text-xs"
              style={{color: "var(--color-text-secondary)"}}
            >
              {campaign.body}
            </p>
            {campaign.imageUrl && (
              <div className="mt-3">
                <img
                  src={campaign.imageUrl}
                  alt="Notification"
                  className="w-full rounded"
                />
              </div>
            )}
          </div>
        </BaseCard>
      </div>

      {/* Sending Progress (Firebase realtime stats) */}
      {campaign.status === "sending" && (
        <BaseCard padding="md">
          <div className="flex items-center justify-between mb-3">
            <h2
              className="text-base font-semibold"
              style={{color: "var(--color-text)"}}
            >
              Sending in Progress...
            </h2>
            <span
              className="text-sm"
              style={{color: "var(--color-text-secondary)"}}
            >
              {formatNumber(campaign.totalSent)} / {formatNumber(campaign.estimatedRecipients)}
            </span>
          </div>
          <div
            className="w-full h-3 rounded-full"
            style={{backgroundColor: "var(--color-surface-2)"}}
          >
            <div
              className="h-full rounded-full transition-all"
              style={{
                width: `${campaign.estimatedRecipients > 0 ? Math.min(100, (campaign.totalSent / campaign.estimatedRecipients) * 100) : 0}%`,
                backgroundColor: "var(--color-primary)",
              }}
            />
          </div>
        </BaseCard>
      )}

      {/* Stats Grid */}
      {(campaign.status === "sent" || campaign.status === "sending") && (
        <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-5 gap-4">
          <BaseCard padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium uppercase tracking-wide mb-2"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  Recipients
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{color: "var(--color-text)"}}
                >
                  {formatNumber(campaign.totalRecipients)}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: "var(--color-primary)15", color: "var(--color-primary)"}}
              >
                <Send size={20} />
              </div>
            </div>
          </BaseCard>

          <BaseCard padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium uppercase tracking-wide mb-2"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  Sent
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{color: "var(--color-text)"}}
                >
                  {formatNumber(campaign.totalSent)}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: "var(--color-info)15", color: "var(--color-info)"}}
              >
                <Send size={20} />
              </div>
            </div>
          </BaseCard>

          <BaseCard padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium uppercase tracking-wide mb-2"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  Delivered
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{color: "var(--color-text)"}}
                >
                  {formatNumber(campaign.totalDelivered)}
                </p>
                <p
                  className="text-xs mt-1"
                  style={{color: "var(--color-success)"}}
                >
                  {formatPercentage(campaign.deliveryRate)}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: "var(--color-success)15", color: "var(--color-success)"}}
              >
                <CheckCircle size={20} />
              </div>
            </div>
          </BaseCard>

          <BaseCard padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium uppercase tracking-wide mb-2"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  Read
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{color: "var(--color-text)"}}
                >
                  {formatNumber(campaign.totalRead)}
                </p>
                <p
                  className="text-xs mt-1"
                  style={{color: "var(--color-accent)"}}
                >
                  {formatPercentage(campaign.readRate)}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: "var(--color-accent)15", color: "var(--color-accent)"}}
              >
                <Eye size={20} />
              </div>
            </div>
          </BaseCard>

          <BaseCard padding="md">
            <div className="flex items-start justify-between">
              <div>
                <p
                  className="text-xs font-medium uppercase tracking-wide mb-2"
                  style={{color: "var(--color-text-secondary)"}}
                >
                  Failed
                </p>
                <p
                  className="text-2xl font-bold"
                  style={{color: "var(--color-text)"}}
                >
                  {formatNumber(campaign.totalFailed)}
                </p>
              </div>
              <div
                className="w-10 h-10 rounded-lg flex items-center justify-center"
                style={{backgroundColor: "var(--color-danger)15", color: "var(--color-danger)"}}
              >
                <XCircle size={20} />
              </div>
            </div>
          </BaseCard>
        </div>
      )}

      {/* Audience Info */}
      {campaign.audienceFilters && Object.keys(campaign.audienceFilters).length > 0 && (
        <BaseCard padding="md">
          <h2
            className="text-base font-semibold mb-3"
            style={{color: "var(--color-text)"}}
          >
            Audience Filters
          </h2>
          <div className="flex flex-col gap-2.5 text-sm">
            {campaign.audienceFilters.countries && campaign.audienceFilters.countries.length > 0 && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Countries:</span>
                <span style={{color: "var(--color-text)"}}>{campaign.audienceFilters.countries.join(", ")}</span>
              </div>
            )}
            {campaign.audienceFilters.gender && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Gender:</span>
                <span style={{color: "var(--color-text)"}}>
                  {campaign.audienceFilters.gender === "M" ? "Male" : "Female"}
                </span>
              </div>
            )}
            {campaign.audienceFilters.device && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Device:</span>
                <span style={{color: "var(--color-text)"}}>
                  {campaign.audienceFilters.device === "ios" ? "iOS" : "Android"}
                </span>
              </div>
            )}
            {campaign.audienceFilters.kyc_status && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>KYC Status:</span>
                <span
                  style={{color: "var(--color-text)"}}
                  className="capitalize"
                >
                  {campaign.audienceFilters.kyc_status.replace("_", " ")}
                </span>
              </div>
            )}
            {campaign.audienceFilters.has_wallet && (
              <div className="flex items-center gap-2">
                <CheckCircle
                  size={14}
                  style={{color: "var(--color-success)"}}
                />
                <span style={{color: "var(--color-text)"}}>Only users with wallets</span>
              </div>
            )}
            {campaign.audienceFilters.last_transaction_before && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Last transaction before:</span>
                <span style={{color: "var(--color-text)"}}>{campaign.audienceFilters.last_transaction_before}</span>
              </div>
            )}
            {campaign.audienceFilters.registration_before && (
              <div className="flex justify-between">
                <span style={{color: "var(--color-text-secondary)"}}>Registration before:</span>
                <span style={{color: "var(--color-text)"}}>{campaign.audienceFilters.registration_before}</span>
              </div>
            )}
          </div>
        </BaseCard>
      )}

      {/* Data Payload */}
      {campaign.dataPayload && Object.keys(campaign.dataPayload).length > 0 && (
        <BaseCard padding="md">
          <h2
            className="text-base font-semibold mb-3"
            style={{color: "var(--color-text)"}}
          >
            Data Payload
          </h2>
          <pre
            className="text-xs p-3 rounded overflow-auto"
            style={{backgroundColor: "var(--color-surface-2)", color: "var(--color-text)"}}
          >
            {JSON.stringify(campaign.dataPayload, null, 2)}
          </pre>
        </BaseCard>
      )}

      {/* Confirm Send Modal */}
      {showConfirmSend && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{backgroundColor: "rgba(0,0,0,0.5)"}}
          onClick={() => setShowConfirmSend(false)}
        >
          <BaseCard
            padding="md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col gap-4 max-w-md">
              <div className="flex items-center gap-3">
                <AlertCircle
                  size={24}
                  style={{color: "var(--color-warning)"}}
                />
                <h3
                  className="text-lg font-semibold"
                  style={{color: "var(--color-text)"}}
                >
                  Send Campaign?
                </h3>
              </div>
              <p
                className="text-sm"
                style={{color: "var(--color-text-secondary)"}}
              >
                You are about to send this notification to <strong>{formatNumber(campaign.totalRecipients)}</strong>{" "}
                users. This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <BaseButton
                  variant="ghost"
                  onClick={() => setShowConfirmSend(false)}
                >
                  Cancel
                </BaseButton>
                <BaseButton
                  variant="primary"
                  onClick={handleSend}
                  disabled={sendCampaign.isPending}
                >
                  {sendCampaign.isPending ? "Sending..." : "Send Campaign"}
                </BaseButton>
              </div>
            </div>
          </BaseCard>
        </div>
      )}

      {/* Confirm Cancel Modal */}
      {showConfirmCancel && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4"
          style={{backgroundColor: "rgba(0,0,0,0.5)"}}
          onClick={() => setShowConfirmCancel(false)}
        >
          <BaseCard
            padding="md"
            onClick={e => e.stopPropagation()}
          >
            <div className="flex flex-col gap-4 max-w-md">
              <div className="flex items-center gap-3">
                <AlertCircle
                  size={24}
                  style={{color: "var(--color-danger)"}}
                />
                <h3
                  className="text-lg font-semibold"
                  style={{color: "var(--color-text)"}}
                >
                  Cancel Campaign?
                </h3>
              </div>
              <p
                className="text-sm"
                style={{color: "var(--color-text-secondary)"}}
              >
                Are you sure you want to cancel this campaign? This action cannot be undone.
              </p>
              <div className="flex gap-3 justify-end">
                <BaseButton
                  variant="ghost"
                  onClick={() => setShowConfirmCancel(false)}
                >
                  No, Keep It
                </BaseButton>
                <BaseButton
                  variant="danger"
                  onClick={handleCancel}
                  disabled={cancelCampaign.isPending}
                >
                  {cancelCampaign.isPending ? "Cancelling..." : "Yes, Cancel"}
                </BaseButton>
              </div>
            </div>
          </BaseCard>
        </div>
      )}

      <BaseAlert
        open={alertState.open}
        variant={alertState.variant}
        title={alertState.title}
        description={alertState.description}
        onClose={() => setAlertState(prev => ({...prev, open: false}))}
      />
    </div>
  );
};
